import 'mapbox-gl/dist/mapbox-gl.css';

import { CurrentPositionPin } from '@application/assets';
import { ExtendMapControl, clusterCountLayer, clusterLayer, comingSoonPointLayer, trackedPointLayer, untrackedPointLayer } from '@application/components';
import { Site } from '@domain/graphql.types';
import { Box, ButtonBase, useTheme } from '@mui/material';
import { type RefObject, useCallback, useContext, useRef } from 'react';
import ReactMap, { Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox';
import { useTranslation } from 'react-i18next';
import { useResizeObserver } from 'usehooks-ts';
import MapPin, { getMapPinVariant, mapPinVariantLabelKey } from '../components/MapPin';
import SiteLookupContext from '../context/SiteLookupContext';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import LegendMapControl from './LegendMapControl';
import MapLoadingStatus from './MapLoadingStatus';
import ContextMenu from './contextmenu/SiteLookupMapContextMenu';
import useSiteLookupMapContextMenu from './contextmenu/useSiteLookupMapContextMenu';
import useSiteLookupMap from './useSiteLookupMap';

type Props = {
  fetchingSites: boolean;
  /** more ports match than the report can contain */
  isReportCapped: boolean;
  sites?: readonly Site[];
};

const SiteLookupMap = ({ fetchingSites, isReportCapped, sites }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { hoveredSiteId, isMapExtended, toggleIsMapExtended } = useContext(SiteLookupMapContext);
  const {
    appliedFilters,
    filters: { address, radius: searchRadius },
    updateFilterAddress,
  } = useContext(SiteLookupContext);
  const center = address?.coordinates;
  const showRadiusArea = !!center;

  const {
    actions: { retryOutOfRadiusSites, setSelectedSite },
    layers: { radiusCircle },
    map: { accessToken, style },
    refs: { map },
    state: { allSites, displayExtendControl, mapCenter, mapZoom, outOfRadiusSitesError, outOfRadiusSitesLoading, selectedSite },
  } = useSiteLookupMap({ appliedFilters, center, searchRadius, sites });

  const {
    actions: { close: closeContextMenu, copyCoordinatesToClipboard, open: openContextMenu, searchNearby },
    state: { coordinates, point, visible: contextMenuIsVisible },
  } = useSiteLookupMapContextMenu({
    onSearch: ({ lat: latitude, lng: longitude, fullAddress, regionCode }: { lat: number; lng: number; fullAddress: string; regionCode: string }) =>
      updateFilterAddress({
        coordinates: {
          latitude,
          longitude,
        },
        fullAddress,
        regionCode,
      }),
  });

  const {
    palette: { primary },
  } = useTheme();

  const onResize = useCallback(() => {
    map.current?.resize();
  }, [map]);

  useResizeObserver({ ref: containerRef as RefObject<HTMLDivElement>, onResize });

  return (
    <Box ref={containerRef} sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <ReactMap
        ref={map}
        mapStyle={style}
        initialViewState={{
          latitude: mapCenter.latitude,
          longitude: mapCenter.longitude,
          zoom: mapZoom,
        }}
        dragRotate={false}
        mapboxAccessToken={accessToken}
        onContextMenu={openContextMenu}
        onClick={closeContextMenu}
      >
        {showRadiusArea && (
          <Source id="map-date-source" type="geojson" data={radiusCircle}>
            <Layer
              beforeId="clusters"
              id="center-radius-circle"
              type="fill"
              paint={{
                'fill-color': primary.dark,
                'fill-opacity': 0.12,
              }}
            />

            <Layer
              beforeId="clusters"
              id="center-radius-outer-line"
              type="line"
              paint={{
                'line-width': 2,
                'line-color': primary.main,
              }}
            />

            {!fetchingSites &&
              sites?.map((site) => {
                const { id, coordinates, distance, includedInReport, name } = site;
                const isSelected = selectedSite?.id === id;
                const isHovered = hoveredSiteId === id;
                const status = t(mapPinVariantLabelKey[getMapPinVariant(site, { isReportCapped })]);
                // The open station's pin is larger (and darker, the selected pin); a hovered list row enlarges its pin.
                const size = isSelected ? 52 : isHovered ? 48 : 40;

                return (
                  <Marker
                    key={id}
                    latitude={coordinates.latitude}
                    longitude={coordinates.longitude}
                    scale={1}
                    style={{ zIndex: isSelected ? 10 : isHovered ? 9 : 'unset' }}
                  >
                    <ButtonBase
                      aria-label={t('siteLookup.list.stationLabel', { name, distance: t('siteLookup.list.distance', { count: +distance.toFixed(2) }), status })}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedSite({ id, origin: 'map' })}
                      sx={{ borderRadius: '50%', lineHeight: 0, '&:focus-visible': { outline: `2px solid ${primary.main}`, outlineOffset: 2 } }}
                    >
                      <MapPin height={size} includedInReport={includedInReport} variant={getMapPinVariant(site, { isReportCapped, isSelected })} width={size} />
                    </ButtonBase>
                  </Marker>
                );
              })}
            <Marker latitude={mapCenter.latitude} longitude={mapCenter.longitude} scale={1} style={{ zIndex: 5 }}>
              <CurrentPositionPin />
            </Marker>
          </Source>
        )}

        {/* GET /sites only returns the stations the tenant may access, so no greyed "not included" layers here. */}
        <Source id="all_sites" type="geojson" data={allSites} cluster={true} clusterMaxZoom={14} clusterRadius={50}>
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...comingSoonPointLayer} />
          <Layer {...trackedPointLayer} />
          <Layer {...untrackedPointLayer} />
        </Source>

        <NavigationControl position="bottom-right" showCompass={false} />

        {displayExtendControl && <ExtendMapControl handleToggleMapExtended={toggleIsMapExtended} isMapExtended={isMapExtended} />}

        <LegendMapControl showReportSelection={isReportCapped} />
        {contextMenuIsVisible && (
          <ContextMenu
            coordinates={coordinates}
            isOpen
            onClose={closeContextMenu}
            onCoordinates={copyCoordinatesToClipboard}
            onSearch={searchNearby}
            position={point}
          />
        )}
      </ReactMap>

      <MapLoadingStatus loading={fetchingSites || outOfRadiusSitesLoading} error={outOfRadiusSitesError} onRetry={retryOutOfRadiusSites} />
    </Box>
  );
};

export default SiteLookupMap;
